import type { TrainingLocation } from '@/api/types/enrollment'
import { ENROLLMENT_LOCATION_MESSAGES } from '../../../constants/enrollmentContent'
import {
  getEnrollmentDraftLocationExternalId,
  getEnrollmentDraftZipCode
} from '../../../utils/enrollmentDraftSelectors'
import type { EligibilityFormData } from '../types'
import type { LocationOption } from './types'

export function getTransmissionLabel(transmission?: string) {
  switch (transmission) {
    case 'AUTOMATIC':
      return 'AUTOMATIC'
    case 'MANUAL':
      return 'Manual only'
    case 'Automatic and Manual':
      return 'Automatic and Manual'
    default:
      return transmission
  }
}

export function getLocationSelectionZipCode(
  formData: EligibilityFormData,
  storedZipCode: string
): string {
  const formZip = formData.zipCode?.trim()
  if (formZip) return formZip

  const storeZip = storedZipCode.trim()
  if (storeZip) return storeZip

  const directZip = getEnrollmentDraftZipCode()
  if (directZip) return directZip

  return ''
}

export function buildLocationOptions(locations: TrainingLocation[]): LocationOption[] {
  return locations
    .map(location => ({
      value: location.externalId || String(location.id),
      label: [location.name, [location.city, location.state].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' - '),
      transmission: getTransmissionLabel(
        location.transmissionAvailable || location.transmission || location.transmissionType
      )
    }))
    .filter(option => Boolean(option.value && option.label))
}

export function getEffectiveLocationSelectionValue(
  formData: EligibilityFormData,
  storedLocationExternalId: string
): string {
  if (formData.location) return formData.location
  if (storedLocationExternalId) return storedLocationExternalId

  const storedLocation = getEnrollmentDraftLocationExternalId()
  if (storedLocation) return storedLocation

  return ''
}

export function getLocationSelectionPlaceholder({
  isMissingDriverLicenseState,
  isLoadingLocations,
  isError,
  hasNoLicenseStateLocations,
  hasNoLocations
}: {
  isMissingDriverLicenseState: boolean
  isLoadingLocations: boolean
  isError: boolean
  hasNoLicenseStateLocations: boolean
  hasNoLocations: boolean
}): string {
  if (isMissingDriverLicenseState) {
    return ENROLLMENT_LOCATION_MESSAGES.missingLicenseStatePlaceholder
  }
  if (isLoadingLocations) return ENROLLMENT_LOCATION_MESSAGES.loadingPlaceholder
  if (isError) return ENROLLMENT_LOCATION_MESSAGES.unavailablePlaceholder
  if (hasNoLicenseStateLocations) {
    return ENROLLMENT_LOCATION_MESSAGES.noLicenseStateLocationsPlaceholder
  }
  if (hasNoLocations) return ENROLLMENT_LOCATION_MESSAGES.noLocationsPlaceholder

  return 'Select Training Location...'
}
